<template>
  <div class="checkout-account">
    <p class="h6">
      Sign Up
    </p>
    <template v-if="!order.business">
      <div class="social-login-buttons">
        <div class="social-btn google-btn">
          <GoogleLoginButton
            :form-type="formType"
            :errored="termsError"
            :terms-agreed="accountCheckout.termsAgreed"
            @check="checkTerms"
            @login="$emit('continue')"
          />
        </div>
        <div class="social-btn">
          <FacebookLoginButton
            :form-type="formType"
            :errored="termsError"
            :terms-agreed="accountCheckout.termsAgreed"
            @check="checkTerms"
            @login="$emit('continue')"
          />
        </div>
        <div class="social-btn">
          <LinkedinLoginButton
            :form-type="formType"
            :errored="termsError"
            :terms-agreed="accountCheckout.termsAgreed"
            @check="checkTerms"
            @login="$emit('continue')"
          />
        </div>
      </div>
      <small v-if="$store.state.auth.loginAlert" class="alert-error">Please use your company email address to log in to your business account.</small>
      <span class="or-line">or</span>
    </template>
    <form
      class="account-details-form"
      autocomplete="chrome-off"
      @submit.prevent="continueCheckout"
    >
      <div class="input-group email-input" :class="{ 'invalid-field': emailAlert }">
        <label for="checkout-email" class="required">Email</label>
        <input
          id="checkout-email"
          v-model="email"
          type="text"
          name="checkout-email"
          autocomplete="email"
          placeholder="Email address"
          :class="{ 'invalid-field': emailAlert }"
          :data-event="`${$route.query.plan}_email.click`"
          data-direct-send
          @input="emailAlert = ''"
        >
        <small v-if="emailAlert" class="alert">{{ emailAlert }}</small>
      </div>
      <Password @input="p => password = p" />
      <div class="input-group">
        <label for="fname" class="required">First Name</label>
        <input
          id="fname" v-model="firstName" type="text"
          name="fname"
          autocomplete="given-name"
          placeholder="Your First Name"
          :class="{ 'invalid-field': firstNameAlert }"
          :data-event="`${$route.query.plan}_firstname.click`"
          data-direct-send
          @input="firstNameAlert = ''"
        >
        <small v-if="firstNameAlert" class="alert">{{ firstNameAlert }}</small>
      </div>
      <div class="input-group">
        <label for="lname" class="required">Last Name</label>
        <input
          id="lname" v-model="lastName" type="text"
          name="lname"
          autocomplete="family-name"
          placeholder="Your Last Name"
          :class="{ 'invalid-field': lastNameAlert }"
          :data-event="`${$route.query.plan}_lastname.click`"
          data-direct-send
          @input="lastNameAlert = ''"
        >
        <small v-if="lastNameAlert" class="alert">{{ lastNameAlert }}</small>
      </div>
      <div v-if="!order.business" class="input-group checkbox">
        <div>
          <input
            id="invoice" v-model="accountCheckout.invoice" type="checkbox"
            class="custom-checkbox reverse-colors"
            :data-event="`${$route.query.plan}_needinvoice.click`"
            data-direct-send
          >
          <label for="invoice">
            I need an invoice
          </label>
        </div>
      </div>
      <div v-if="accountCheckout.invoice || order.business" class="invoice-info account-details-form">
        <div class="input-group">
          <label for="companyName" class="required">Company Name</label>
          <input
            id="companyName" v-model="accountCheckout.companyName" type="text"
            name="companyName"
            autocomplete="organization"
            placeholder="Company Name"
            :class="{ 'invalid-field': companyNameAlert }"
            @input="lastNameAlert = ''"
          >
          <small v-if="companyNameAlert" class="alert">{{ companyNameAlert }}</small>
        </div>
        <div class="input-group">
          <div class="companyId-question-icon">
            <label for="companyId" class="required">Company ID</label>
            <div class="question-icon">
              <IconQuestion />
              <Tooltip>
                <ul>
                  <li>
                    USA - EIN (Employer Identification Number)
                  </li>
                  <li>
                    India - CIN (Corporate Identification Number)
                  </li>
                  <li>
                    Canada - BN (Business Number)
                  </li>
                  <li>
                    Australia - ACN (Australian Company Number)
                  </li>
                  <li>
                    Europe - EUID (European Unique Identifier)
                  </li>
                </ul>
              </Tooltip>
            </div>
          </div>
          <input
            id="companyId" v-model="accountCheckout.companyId" type="text"
            name="companyId"
            autocomplete="organization"
            placeholder="Company ID"
            :class="{ 'invalid-field': companyIdAlert }"
            @input="companyIdAlert = ''"
          >
          <small v-if="companyIdAlert" class="alert">{{ companyIdAlert }}</small>
        </div>
        <div class="input-group">
          <label for="vat">EU/UK VAT Number (optional)</label>
          <input
            id="vat" v-model="accountCheckout.vat" type="text"
            name="vat"
            autocomplete="organization"
            placeholder="Number"
            :class="{ 'invalid-field': vatAlert }"
            @input="vatAlert = ''"
          >
          <small v-if="vatAlert" class="alert">{{ vatAlert }}</small>
        </div>
      </div>
      <div class="input-group checkbox">
        <div :class="{ error: termsError }">
          <input
            id="terms" v-model="accountCheckout.termsAgreed" type="checkbox"
            class="custom-checkbox reverse-colors"
            :data-event="`${$route.query.plan}_agreewithterms.click`"
            data-direct-send
            @change="termsError = false"
          >
          <label for="terms">
            I agree with {{ $config.platformName }}’s
            <nuxt-link class="link-accent" target="_blank" to="/terms-of-use/">Terms of Use</nuxt-link>
            and
            <nuxt-link class="link-accent" target="_blank" to="/privacy-policy/">Privacy Policy</nuxt-link><span class="label-required">*</span>
          </label>
        </div>
        <small v-if="termsError" class="alert alert-privacy">Please agree to the terms of use and privacy policy</small>
      </div>
      <button
        class="btn-primary btn-lg submit h6"
        type="submit"
        :disabled="!canRegister && $config.platform === 'ds'"
        :data-event="`${$route.query.plan}_continue.click`"
        data-direct-send
      >
        Continue
      </button>
    </form>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import Tooltip from '@itbpbg/lms-components/src/components/Tooltip';
import Password from '../Forms/Password';
import FacebookLoginButton from './FacebookLoginButton';
import GoogleLoginButton from './GoogleLoginButton';
import LinkedinLoginButton from './LinkedinLoginButton';
import IconQuestion from '../../assets/svg/icon-question.svg?vue-component';

export default {
  components: {
    Password,
    FacebookLoginButton,
    GoogleLoginButton,
    LinkedinLoginButton,
    IconQuestion,
    Tooltip,
  },
  data() {
    return {
      accountCheckout: {
        invoice: false,
        companyName: '',
        companyId: '',
        vat: '',
        termsAgreed: false,
        checkout: true,
      },
      passwordValidateInfo: '',
      passwordValidateText: '',
      emailAlert: '',
      passwordAlert: '',
      lastNameAlert: '',
      firstNameAlert: '',
      companyNameAlert: '',
      companyIdAlert: '',
      vatAlert: '',
      formType: 'signup',
      signedUp: false,
      termsError: false,
      loading: false,
    };
  },
  computed: {
    ...mapState('checkout', ['order', 'account', 'paymentInfo']),
    email: {
      get() {
        return this.account.email;
      },
      set(value) {
        this.$store.commit('checkout/setAccountEmail', value);
      },
    },
    password: {
      get() {
        return this.account.password;
      },
      set(value) {
        this.$store.commit('checkout/setAccountPassword', value);
      },
    },
    firstName: {
      get() {
        return this.account.firstName;
      },
      set(value) {
        this.$store.commit('checkout/setAccountFirstName', value);
      },
    },
    lastName: {
      get() {
        return this.account.lastName;
      },
      set(value) {
        this.$store.commit('checkout/setAccountLastName', value);
      },
    },
    canRegister() {
      const invoice = this.accountCheckout.invoice || this.order.business ? this.accountCheckout.companyName && this.accountCheckout.companyId && this.accountCheckout.companyId : true;

      return this.email
        && this.password
        && this.password.length >= 6
        && this.firstName
        && this.lastName
        && this.accountCheckout.termsAgreed
        && invoice
        && !this.loading;
    },
  },
  async created() {
    this.$root.$on('check-terms', () => {
      this.checkTerms();
    });
  },
  mounted() {
    if (this.$route.query.error && this.$route.query.error === 'business') {
      this.$store.commit('auth/setLoginAlert', true);
      const query = { ...this.$route.query };
      delete query.error;
      this.$router.replace({ query });
    }
    const sendCartData = () => {
      if (window.location.pathname.includes('/checkout/') && this.$validEmail(this.email)) {
        navigator.sendBeacon(`${window.$nuxt.$config.backendUrl}/payments/checkout/cart`, new Blob([JSON.stringify({
          plan: this.paymentInfo.plan.name,
          type: 'no-user',
          email: this.email,
          firstName: this.firstName,
          lastName: this.lastName,
        })], { type: 'application/json' }));
      }
    };

    document.addEventListener(this.$getBrowserVisibilityProp(), () => {
      const hiddenProp = this.$getBrowserDocumentHiddenProp();

      if (hiddenProp) {
        if (document.visibilityState === this.$getBrowserDocumentHiddenProp()) {
          sendCartData();
        }
      }
    });

    if (!this.$getBrowserVisibilityProp()) {
      window.addEventListener('pagehide', () => {
        sendCartData();
      });
    }
  },
  methods: {
    checkTerms() {
      if (!this.accountCheckout.termsAgreed) {
        this.termsError = true;
        return this.termsError;
      }
      this.termsError = false;
      return this.termsError;
    },
    async continueCheckout() {
      try {
        if (!this.signedUp) {
          if (!this.$validEmail(this.email.trim())) {
            this.emailAlert = 'Invalid email';
            this.$store.commit('checkout/setExistingEmail', '');
            return;
          }

          if (!this.firstName) {
            this.firstNameAlert = 'Invalid First Name';
            return;
          }
          if (!this.lastName) {
            this.lastNameAlert = 'Invalid Last Name';
            return;
          }
        }

        if (this.accountCheckout.invoice || this.order.business) {
          if (!this.accountCheckout.companyName) {
            this.companyNameAlert = 'Invalid Company Name';
            return;
          }
          if (!this.accountCheckout.companyId) {
            this.companyIdAlert = 'Invalid Company ID';
            return;
          }
        }

        if (!this.accountCheckout.termsAgreed) {
          this.termsError = true;
          return;
        }

        const data = {
          ...this.accountCheckout,
          name: `${this.firstName} ${this.lastName}`,
          email: this.email.trim(),
          password: this.password,
          firstName: this.firstName,
          lastName: this.lastName,
          rkey: this.$route.query.rkey,
          noError: true,
          coursePurchase: this.$route.query.ptype === 'individual-course',
        };

        if (this.order.business) {
          this.loading = true;
          const signup = await this.$axios.$post('/business/admin/signup', data, { noError: true }).catch((e) => e.response.data);
          this.loading = false;

          if (signup && signup.error === 'emailAlreadyTaken') {
            this.emailAlert = 'This email is already registered. Please log in.';
            this.$store.commit('checkout/setExistingEmail', data.email);
            return;
          }
          this.$store.commit('checkout/setExistingEmail', '');

          if (signup && signup.error === 'vatInvalid') {
            this.vatAlert = 'Invalid Vat Number';
            return;
          }

          if (signup && signup.accessToken && signup.refreshToken) {
            this.$store.dispatch('auth/setUserInfo', signup.user);
          }

          this.$emit('continue');
          return;
        }

        if (!this.signedUp) {
          const signup = await this.$store.dispatch('auth/register', data);

          if (signup && signup.error === 'emailAlreadyTaken') {
            this.emailAlert = 'This email is already registered. Please log in.';
            this.$store.commit('checkout/setExistingEmail', data.email);
            return;
          }
          this.$store.commit('checkout/setExistingEmail', '');

          if (signup && signup.status === 'error') {
            this.emailAlert = signup.message;
            return;
          }

          if (signup && signup.status !== 'error') {
            this.$tracking.setSignupEvent({ signupType: 'Direct', directSend: true, userId: this.$store.getters['auth/user'].id });
            this.signedUp = true;
          }
        }

        if (this.signedUp) {
          if (data.invoice) {
            try {
              await this.$axios.$post('/payments/invoices', data, { noError: true });
            } catch (e) {
              if (e?.response?.data?.error === 'vatInvalid') {
                this.vatAlert = 'Invalid Vat Number';
                return;
              }
            }
          }

          this.$emit('continue');
        }
      } catch (error) {
        console.log('catch err => ', error);
      }
    },
  },
};
</script>

<style lang="scss">
.checkout-account {
  .h6 {
    text-transform: capitalize;
    color: var(--main-light-shade-3);
    margin-bottom: var(--spacing-32);
  }

  .social-login-buttons {
    display: flex;
    align-items: center;
    justify-content: space-between;

    .social-btn {
      background: var(--main-dark-shade-3);
      border: 1px solid var(--dark-gray);
      box-sizing: border-box;
      border-radius: 5px;
      display: flex;
      justify-content: center;
      align-items: center;
      width: 100%;
      height: 52px;

      &:nth-of-type(2) {
        margin: 0 var(--spacing-24);
      }
    }
  }

  .or-line {
    display: grid;
    grid-template-columns: 1fr max-content 1fr;
    grid-column-gap: 22px;
    align-items: center;
    margin: var(--spacing-16) 0;

    &:before,
    &:after {
      content: "";
      display: block;
      height: 1px;
      background-color: var(--main-dark-light-3);
    }
  }

  .alert-error {
    position: static;
    text-align: center;
    display: block;
    margin-top: var(--spacing-8);
    height: auto;
    background-color: var(--main-red-light-2);
    border: 1px solid var(--main-red-light-3);
    border-radius: 5px;
    color: var(--main-red-shade-1);
    font-weight: 500;
    font-size: 14px;
    line-height: 130%;
    padding: 12px 30px;
  }

  .account-details-form {
    display: flex;
    flex-wrap: wrap;

    .invoice-info {
      margin-top: var(--spacing-10);
      margin-bottom: var(--spacing-36);
    }

    .input-group {
      width: 50%;
      position: relative;

      .companyId-question-icon {
        display: flex;
        align-items: center;

        .question-icon {
          display: flex;
          align-items: center;
          width: 12px;
          height: 12px;
          margin-left: 5px;
          margin-bottom: 5px;

          &:hover {
            .tooltip {
              width: 358px;
              padding: 0;
              right: 0;
              display: flex;
              justify-content: center;
              bottom: 105px;
              align-items: center;
              background: var(--true-white);
              border: 1px solid var(--true-white);
              box-shadow: 0 var(--spacing-4) 10px rgba(0, 0, 0, 0.1);

              &:after {
                margin-bottom: 5px;
                border-top: 5px solid var(--true-white);
                left: 118px;
              }

              ul {
                padding: 18px var(--spacing-8) 18px;
                margin: 0;

                li {
                  font-family: 'Mazzard', sans-serif;
                  font-size: var(--spacing-14);
                  line-height: var(--spacing-20px);
                  color: var(--main-dark-shade-0);
                  white-space: nowrap;
                }
              }
            }
          }
        }
      }

      &:nth-of-type(1),
      &:nth-of-type(3) {
        padding-right: 15px;
      }

      &:nth-of-type(2),
      &:nth-of-type(4) {
        padding-left: 15px;
      }

      &:nth-of-type(1) {
        &:not(.invalid-field) {
          padding-bottom: var(--spacing-24);
        }
      }

      &:nth-of-type(4) {
        padding-bottom: var(--spacing-36);
      }

      &:nth-of-type(5) {
        padding-bottom: 14px;
      }

      &.invalid-field {
        &.email-input {
          .alert {
            color: var(--main-red);
          }
        }
      }

      .invalid-field + .alert {
        color: var(--main-red);
      }

      label {
        display: inline-block;
        padding-bottom: 5px;
        font-size: 14px;
        color: var(--main-light);

        &:after {
          padding-left: 3px;
        }
      }

      input {
        text-overflow: ellipsis;
        background: #4B5461;
        border: 1px solid #565E6A;
        border-radius: 5px;
        color: var(--main-light-shade-4);

        &::placeholder {
          color: var(--main-light-shade-4);
        }

        &.invalid-field {
          border-color: var(--main-red);
        }

        &:-webkit-autofill,
        &:-webkit-autofill:hover,
        &:-webkit-autofill:focus,
        &:-webkit-autofill:active {
          -webkit-box-shadow: 0 0 0 50px #4B5461 inset !important;
          -webkit-text-fill-color: var(--main-light);
        }
      }
    }

    .input-group.checkbox {
      width: 100%;
      position: relative;

      .custom-checkbox {
        position: absolute;
      }

      label {
        color: var(--main-dark-shade-4);

        &:after {
          border-color: var(--main-dark-shade-4);
        }
      }

      .label-required {
        color: var(--main-red-shade-1);
        padding-left: 3px;
      }

      .error {
        label {
          &:after {
            border-color: var(--main-red);
          }
        }
      }

      .alert {
        color: var(--main-red);
      }
    }

    button {
      margin-top: var(--spacing-36);
      width: 100%;
      max-width: 200px;
      font-weight: 600;
      margin-bottom: 0;
    }
  }

  @include bp($bp-mobile) {
    .social-login-buttons {
      .social-btn:not(.google-btn) {
        max-width: 52px;

        &:nth-of-type(2) {
          margin: 0 var(--spacing-8);
        }
      }
    }

    .account-details-form {
      .input-group:not(.checkbox) {
        width: 100%;
        padding: 0 !important;
        margin-bottom: var(--spacing-40);
      }

      button {
        max-width: 100%;
      }
    }

    .account-details-form .input-group .companyId-question-icon .question-icon:hover .tooltip {
      width: 100%;
      min-height: 199px;
      transform: unset;
      left: 0;
      bottom: 79px;

      &:after {
        left: var(--spacing-100);
      }

      ul {
        li {
          white-space: normal;
        }
      }
    }
  }

  @include bp($bp-tablet) {
    .account-details-form .input-group .companyId-question-icon .question-icon:hover .tooltip {
      width: 358px;
      min-height: unset;
      transform: unset;
      left: 0;
      bottom: 79px;

      &:after {
        left: 100px;
      }

      ul {
        li {
          white-space: normal;
        }
      }
    }
  }
}

.fa {
  .checkout-account {
    .h6 {
      color: var(--main-light);
    }

    .account-details-form {
      button {
        margin-top: var(--spacing-32);
      }

      .input-group {
        input {
          color: var(--main-light);
          background: var(--main-dark-light-2);
          border: 1px solid var(--main-dark-light-3);

          &.invalid-field {
            border-color: #E04A4A;
          }

          &:-webkit-autofill,
          &:-webkit-autofill:hover,
          &:-webkit-autofill:focus,
          &:-webkit-autofill:active {
            -webkit-box-shadow: 0 0 0 50px var(--main-dark-light-2) inset !important;
          }
        }

        .invalid-field + .alert {
          color: #E04A4A;
        }

        label {
          &:after {
            color: #E04A4A;
          }
        }
      }

      .input-group.checkbox {
        .alert {
          font-size: 12px;
        }
      }
    }

    .social-login-buttons {
      .social-btn {
        background: var(--main-dark-light-2);
        border: 1px solid var(--main-dark-light-3);
      }
    }
  }

  @include bp($bp-mobile) {
    .account-details-form {
      .input-group:not(.checkbox) {
        margin-bottom: var(--spacing-24);
      }

      .input-group label:before,
      .input-group label:after {
        top: 0;
        margin-top: 0;
      }

      .input-group label:before {
        top: 1px;
      }
    }
  }
}
</style>
